import React, { forwardRef, useState, useRef, useCallback, useEffect } from "react";
import { Input } from "../ui/input";
import { cn } from "@/utils/classnames";

interface NumberInputProps {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  type?: "percentage" | "currency" | "number";
  className?: string;
  error?: boolean;
}

const numberFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  ({ className, value, onChange, type = "number", error, ...props }, ref) => {
    const [internalValue, setInternalValue] = useState(value || 0);
    const [displayValue, setDisplayValue] = useState(
      value !== undefined ? numberFormatter.format(value) : "0.00"
    );
    const inputRef = useRef<HTMLInputElement>(null);

    // Update internal state when external value changes
    useEffect(() => {
      if (value !== undefined) {
        setInternalValue(value);
        setDisplayValue(numberFormatter.format(value));
      } else {
        setInternalValue(0);
        setDisplayValue("0.00");
      }
    }, [value]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
      // Allow navigation keys
      if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
        return;
      }

      // Handle minus sign - toggle negative
      if (e.key === '-') {
        e.preventDefault();
        const newValue = -internalValue;
        setInternalValue(newValue);
        setDisplayValue(numberFormatter.format(newValue));
        onChange(newValue);
        return;
      }

      // Only allow digits
      if (!/\d/.test(e.key)) {
        e.preventDefault();
        return;
      }

      // Handle digit input
      e.preventDefault();
      const digit = e.key;
      const isNegative = internalValue < 0 || Object.is(internalValue, -0);
      const absValue = Math.abs(internalValue);
      const cents = Math.round(absValue * 100);
      const newCents = cents * 10 + parseInt(digit);
      const newAbsValue = newCents / 100;
      const newValue = isNegative ? -newAbsValue : newAbsValue;
      
      setInternalValue(newValue);
      setDisplayValue(numberFormatter.format(newValue));
      onChange(newValue);
    }, [internalValue, onChange]);

    const handleKeyUp = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace') {
        const isNegative = internalValue < 0 || Object.is(internalValue, -0);
        const absValue = Math.abs(internalValue);
        const cents = Math.round(absValue * 100);
        const newCents = Math.floor(cents / 10);
        const newAbsValue = newCents / 100;
        
        let newValue;
        if (newCents === 0) {
          // If we're going to zero and currently at -0, clear the sign
          if (Object.is(internalValue, -0)) {
            newValue = 0; // Clear negative sign
          } else {
            newValue = isNegative ? -0 : 0;
          }
        } else {
          newValue = isNegative ? -newAbsValue : newAbsValue;
        }
        
        setInternalValue(newValue);
        setDisplayValue(numberFormatter.format(newValue));
        onChange(newValue);
      }
    }, [internalValue, onChange]);

    return (
      <div className="relative flex">
        <Input
          ref={(el) => {
            inputRef.current = el;
            if (typeof ref === 'function') {
              ref(el);
            } else if (ref) {
              ref.current = el;
            }
          }}
          value={displayValue}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          onChange={() => {}} // Prevent default onChange behavior
          className={cn("peer", {
            "pe-12": type === "percentage",
            "ps-6": type === "currency",
          })}
          {...props}
        />
        {type === "currency" && (
          <span className="text-muted-foreground pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 pt-0.5 text-sm peer-disabled:opacity-50">
            $
          </span>
        )}
        {type === "percentage" && (
          <span className="text-muted-foreground pointer-events-none absolute inset-y-0 end-0 flex items-center justify-center pe-3 text-sm peer-disabled:opacity-50">
            %
          </span>
        )}
      </div>
    );
  }
);
NumberInput.displayName = "NumberInput";

export default NumberInput;
